import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, X, Filter, Users, MapPin, User, Briefcase, DollarSign, Grid3X3, List, Download, Phone, CreditCard, Heart, Shield, IdCard, Eye, Edit, Book, Calendar, ActivityIcon, PanelTopInactive, ShieldBan } from 'lucide-react';
import { flattenEmployeeDataT } from '../../utils';
import EmployeeCheckListModal from '../../Modal/EmployeeCheckListModal';
import EmployeeTableDataGrid from '../../components/EmployeeTableDataGrid';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SuperannuationFilter from '../../components/Superannuation';
import { useEmployeeStream } from '../../hooks/EmployeStream';
import FilterSubSection from '../../components/FilterSubsection';
import AgeRangeFilter from '../../components/AgeFilter';
import SalaryRangeFilter from '../../components/SalaryRangeFilter';

// FILTER CONFIGURATION - Enable/Disable filter fields here
const FILTER_CONFIG = {
  personalInformation: {
    enabled: true,
    fields: {
      firstName: { enabled: false, hasSearch: true },
      middleName: { enabled: false, hasSearch: true },
      lastName: { enabled: false, hasSearch: true },
      employeeName: { enabled: true, hasSearch: true },
      fatherName: { enabled: false, hasSearch: true },
      motherName: { enabled: false, hasSearch: true },
      placeOfBirth: { enabled: false, hasSearch: true },
      gender: { enabled: true, hasSearch: false },
      nationality: { enabled: false, hasSearch: false },
      religion: { enabled: false, hasSearch: false },
      maritalStatus: { enabled: true, hasSearch: false },
      bloodGroup: { enabled: false, hasSearch: false },
      languagesKnown: { enabled: false, hasSearch: true },
    }
  },
  addressInformation: {
    enabled: false,
    fields: {
      village: { enabled: true, hasSearch: true },
      post: { enabled: false, hasSearch: true },
      district: { enabled: true, hasSearch: true },
      state: { enabled: true, hasSearch: true },
      pinCode: { enabled: true, hasSearch: true }
    }
  },
  contactInformation: {
    enabled: false,
    fields: {
      emergencyContactName: { enabled: true, hasSearch: true }
    }
  },
  employmentDetails: {
    enabled: true,
    fields: {
      designation: { enabled: true, hasSearch: true },
      grade: { enabled: true, hasSearch: true },
      location: { enabled: true, hasSearch: true },
      businessUnit: { enabled: true, hasSearch: true },
      division: { enabled: true, hasSearch: true },
      reportingManager: { enabled: true, hasSearch: true },
      sourceOfCandidate: { enabled: false, hasSearch: false },
      probationPeriod: { enabled: false, hasSearch: false },
      retirementAge: { enabled: false, hasSearch: false },
      department: { enabled: true, hasSearch: true },
      lengthOfService: { enabled: true, hasSearch: true },
    }
  },
  salaryCompensation: {
    enabled: true // Salary range filter
  },
  ageFilter: {
    enabled: true // Age range filter
  },
  bankDetails: {
    enabled: false,
    fields: {
      bankName: { enabled: false, hasSearch: true }
    }
  },
  nomineeInformation: {
    enabled: false,
    fields: {
      nomineeRelationship: { enabled: true, hasSearch: false },
      sharePercentage: { enabled: true, hasSearch: false }
    }
  },
  declarationsCompliance: {
    enabled: false,
    fields: {
      physicalDisabilities: { enabled: true, hasSearch: false },
      majorIllness: { enabled: false, hasSearch: false },
      courtCases: { enabled: true, hasSearch: false },
      policeCustody: { enabled: false, hasSearch: false },
      declarationLocation: { enabled: true, hasSearch: true }
    }
  },
  identificationDocuments: {
    enabled: false,
    fields: {
      aadhaarNumber: { enabled: true, hasSearch: true },
      panNumber: { enabled: true, hasSearch: true }
    }
  },
  educationDetails: {
    enabled: true,
    fields: {
      highestQualification: { enabled: true, hasSearch: true },
      tenth: { enabled: true, hasSearch: true },
      twelfth: { enabled: true, hasSearch: true },
      graduation: { enabled: true, hasSearch: true },
      postGraduation: { enabled: true, hasSearch: true },
      professional: { enabled: true, hasSearch: true }
    }
  },
  superannuationDetails: {
    enabled: true,
    fields: {
      superannuationScheme: { enabled: true, hasSearch: true },
      superannuationNumber: { enabled: true, hasSearch: true }
    }
  }
};

// Helper function to check if a group has any enabled fields
const hasEnabledFields = (groupKey) => {
  if (!FILTER_CONFIG[groupKey]?.enabled) return false;
  
  if (groupKey === 'salaryCompensation' || groupKey === 'ageFilter') return true;
  
  return Object.values(FILTER_CONFIG[groupKey].fields || {}).some(field => field.enabled);
};

// Helper function to parse length of service filter values
const parseLengthOfServiceFilters = (filterValues) => {
  const filters = {
    total: null,
    current: null, 
    previous: null
  };

  filterValues.forEach(value => {
    if (value.includes('total:')) {
      const [min, max] = value.replace('total:', '').split('-').map(parseFloat);
      filters.total = { min, max };
    } else if (value.includes('current:')) {
      const [min, max] = value.replace('current:', '').split('-').map(parseFloat);
      filters.current = { min, max };
    } else if (value.includes('previous:')) {
      const [min, max] = value.replace('previous:', '').split('-').map(parseFloat);
      filters.previous = { min, max };
    }
  });

  return filters;
};

const FilterDropdown = ({ title, icon: Icon, children, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
          isActive 
            ? 'bg-blue-50 border-blue-200 text-blue-700' 
            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{title}</span>
        {isActive && (
          <span className="bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
            !
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div
            className="fixed inset-0"
            style={{ zIndex: 999 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-h-96 overflow-y-auto"
            style={{ zIndex: 1000 }}
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
            {children}
          </div>
        </>
      )}
    </div>
  );
};

// Length of Service Range Filter Component
const LengthOfServiceFilter = ({ options, selectedValues, onChange }) => {
  const [activeTab, setActiveTab] = useState('total');
  
  // Calculate min/max for each service type
  const serviceRanges = useMemo(() => {
    if (!options) {
      return {
        total: { min: 0, max: 30 },
        currentOrg: { min: 0, max: 20 },
        previousOrg: { min: 0, max: 25 }
      };
    }

    const calculateRange = (serviceArray) => {
      if (!serviceArray || serviceArray.length === 0) {
        return { min: 0, max: 10 };
      }
      const numbers = serviceArray.map(years => parseFloat(years)).filter(num => !isNaN(num)); 
      return {
        min: Math.min(...numbers, 0),
        max: Math.max(...numbers, 10)
      };
    };

    return {
      total: calculateRange(options.total),
      currentOrg: calculateRange(options.currentOrg),
      previousOrg: calculateRange(options.previousOrg)
    };
  }, [options]);

  // Parse current filter values
  const parseFilterValues = (values) => {
    const parsed = {
      total: null,
      currentOrg: null,
      previousOrg: null
    };

    values.forEach(value => {
      if (value.includes('total:')) {
        const [min, max] = value.replace('total:', '').split('-').map(parseFloat);
        parsed.total = { min, max };
      } else if (value.includes('current:')) {
        const [min, max] = value.replace('current:', '').split('-').map(parseFloat);
        parsed.currentOrg = { min, max };
      } else if (value.includes('previous:')) {
        const [min, max] = value.replace('previous:', '').split('-').map(parseFloat);
        parsed.previousOrg = { min, max };
      }
    });

    return parsed;
  };

  const currentFilters = parseFilterValues(selectedValues);

  // Local state for each range filter
  const [ranges, setRanges] = useState({
    total: currentFilters.total || serviceRanges.total,
    currentOrg: currentFilters.currentOrg || serviceRanges.currentOrg,
    previousOrg: currentFilters.previousOrg || serviceRanges.previousOrg
  });

  // Update local state when service ranges change
  useEffect(() => {
    setRanges(prev => ({
      total: currentFilters.total || serviceRanges.total,
      currentOrg: currentFilters.currentOrg || serviceRanges.currentOrg,
      previousOrg: currentFilters.previousOrg || serviceRanges.previousOrg
    }));
  }, [serviceRanges, selectedValues]);

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Debounced onChange function
  const debouncedOnChange = useMemo(
    () => debounce((newValues) => {
      onChange(newValues);
    }, 500),
    [onChange]
  );

  // Update filter values
  const updateRangeFilter = (serviceType, newRange) => {
    setRanges(prev => ({
      ...prev,
      [serviceType]: newRange
    }));

    // Create new filter values array
    const newValues = [];
    
    // Add other service types that aren't being updated
    Object.entries(ranges).forEach(([type, range]) => {
      if (type !== serviceType) {
        const defaultRange = serviceRanges[type];
        if (range.min !== defaultRange.min || range.max !== defaultRange.max) {
          newValues.push(`${type === 'currentOrg' ? 'current' : type === 'previousOrg' ? 'previous' : type}:${range.min}-${range.max}`);
        }
      }
    });

    // Add the updated service type
    const defaultRange = serviceRanges[serviceType];
    if (newRange.min !== defaultRange.min || newRange.max !== defaultRange.max) {
      newValues.push(`${serviceType === 'currentOrg' ? 'current' : serviceType === 'previousOrg' ? 'previous' : serviceType}:${newRange.min}-${newRange.max}`);
    }

    debouncedOnChange(newValues);
  };

  // Range slider component
  const RangeSlider = ({ serviceType, range, onChange }) => {
    const defaultRange = serviceRanges[serviceType];
    
    return (
      <div className="space-y-3">
        <div className="flex justify-between text-xs text-gray-600">
          <span>{range.min} years</span>
          <span>{range.max} years</span>
        </div>
        
        <div className="relative px-3">
          <div className="relative h-6 flex items-center">
            {/* Track background */}
            <div className="w-full h-2 bg-gray-200 rounded-lg absolute"></div>
            
            {/* Active range highlight */}
            <div 
              className="h-2 bg-blue-500 rounded-lg absolute"
              style={{
                left: `${((range.min - defaultRange.min) / (defaultRange.max - defaultRange.min)) * 100}%`,
                width: `${((range.max - range.min) / (defaultRange.max - defaultRange.min)) * 100}%`
              }}
            ></div>
            
            {/* Min Range Slider */}
            <input
              type="range"
              min={defaultRange.min}
              max={defaultRange.max}
              step="0.1"
              value={range.min}
              onChange={(e) => {
                const newMin = parseFloat(e.target.value);
                if (newMin <= range.max) {
                  onChange({ ...range, min: newMin });
                }
              }}
              className="absolute w-full h-2 bg-transparent rounded-lg appearance-none cursor-pointer slider-thumb z-10"
            />
            
            {/* Max Range Slider */}
            <input
              type="range"
              min={defaultRange.min}
              max={defaultRange.max}
              step="0.1"
              value={range.max}
              onChange={(e) => {
                const newMax = parseFloat(e.target.value);
                if (newMax >= range.min) {
                  onChange({ ...range, max: newMax });
                }
              }}
              className="absolute w-full h-2 bg-transparent rounded-lg appearance-none cursor-pointer slider-thumb z-20"
            />
          </div>
        </div>

        {/* Manual Input Fields */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Min Years</label>
            <input
              type="number"
              min={defaultRange.min}
              max={defaultRange.max}
              step="0.1"
              value={range.min}
              onChange={(e) => {
                const newMin = parseFloat(e.target.value) || defaultRange.min;
                if (newMin <= range.max && newMin >= defaultRange.min) {
                  onChange({ ...range, min: newMin });
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max Years</label>
            <input
              type="number"
              min={defaultRange.min}
              max={defaultRange.max}
              step="0.1"
              value={range.max}
              onChange={(e) => {
                const newMax = parseFloat(e.target.value) || defaultRange.max;
                if (newMax >= range.min && newMax <= defaultRange.max) {
                  onChange({ ...range, max: newMax });
                }
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Reset to default button */}
        <button
          onClick={() => onChange(defaultRange)}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          Reset to default range
        </button>
      </div>
    );
  };

  const hasActiveFilters = selectedValues.length > 0;

  return (
    <div className="space-y-4">
      {/* Custom CSS for range sliders */}
      <style jsx>{`
        .slider-thumb {
          -webkit-appearance: none;
          background: transparent;
        }
        
        .slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          background: #3b82f6;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          border: 2px solid #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          cursor: pointer;
        }

        .slider-thumb::-webkit-slider-thumb:hover {
          background: #2563eb;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }

        .slider-thumb::-moz-range-thumb {
          background: #3b82f6;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          border: 2px solid #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          cursor: pointer;
          border: none;
        }

        .slider-thumb::-moz-range-thumb:hover {
          background: #2563eb;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }

        .slider-thumb::-moz-range-track {
          background: transparent;
          height: 8px;
          border: none;
        }
      `}</style>
      
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('total')}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            activeTab === 'total'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Total Exp
        </button>
        <button
          onClick={() => setActiveTab('current')}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            activeTab === 'current'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Current Org
        </button>
        <button
          onClick={() => setActiveTab('previous')}
          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            activeTab === 'previous'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Previous Orgs
        </button>
      </div>

      {/* Tab Content */}
      <div className="mt-3">
        {activeTab === 'total' && (
          <div>
            <h5 className="text-xs font-medium text-gray-700 mb-3">Total Experience Range (Current + Previous)</h5>
            <RangeSlider
              serviceType="total"
              range={ranges.total}
              onChange={(newRange) => updateRangeFilter('total', newRange)}
            />
          </div>
        )}
        {activeTab === 'current' && (
          <div>
            <h5 className="text-xs font-medium text-gray-700 mb-3">Current Organization Experience Range</h5>
            <RangeSlider
              serviceType="currentOrg"
              range={ranges.currentOrg}
              onChange={(newRange) => updateRangeFilter('currentOrg', newRange)}
            />
          </div>
        )}
        {activeTab === 'previous' && (
          <div>
            <h5 className="text-xs font-medium text-gray-700 mb-3">Previous Organizations Experience Range</h5>
            <RangeSlider
              serviceType="previousOrg"
              range={ranges.previousOrg}
              onChange={(newRange) => updateRangeFilter('previousOrg', newRange)}
            />
          </div>
        )}
      </div>

      {/* Clear All Filters Button */}
      {hasActiveFilters && (
        <button
          onClick={() => {
            setRanges({
              total: serviceRanges.total,
              currentOrg: serviceRanges.currentOrg,
              previousOrg: serviceRanges.previousOrg
            });
            onChange([]);
          }}
          className="text-xs text-red-600 hover:text-red-800 font-medium"
        >
          Clear All Filters ({selectedValues.length})
        </button>
      )}
    </div>
  );
};

const EmployeeCard = ({ employee }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const pathLocation = useLocation();
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {employee.personal_information?.employee_name}
          </h3>
          <p className="text-blue-600 font-medium text-sm">
            {employee.employment_details?.designation}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              employee.employment_details?.grade === "E2"
                ? "bg-green-100 text-green-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {employee.employment_details?.grade || "N/A"}
          </span>

          <button
            onClick={() => navigate(`/employee-document-access/${employee.eid}`)}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-600"
            title="View Document"
          >
            <Eye size={18} />
          </button>
          {user?.right === 'ADMIN' && (
            <button
              onClick={() => navigate(`/employee-edit/${employee.eid}${pathLocation.pathname === '/employees/inactive' ? '_inactive' : ''}`)}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-600"
              title="Edit Employee"
            >
              <Edit size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
        <div>
          <span className="font-medium">Location:</span>
          <div className="text-gray-800">
            {employee.employment_details?.location || "N/A"}
          </div>
        </div>
        <div>
          <span className="font-medium">State:</span>
          <div className="text-gray-800">
            {employee.address_information?.permanent_address?.state || "N/A"}
          </div>
        </div>
        <div>
          <span className="font-medium">District:</span>
          <div className="text-gray-800">
            {employee.address_information?.permanent_address?.district || "N/A"}
          </div>
        </div>
        <div>
          <span className="font-medium">Status:</span>
          <div className="text-gray-800">
            {employee.personal_information?.marital_status || "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewModeToggle = ({ viewMode, onViewModeChange, data = [] }) => {
  const exportToCSV = (data, fileName = 'employees_export.csv') => {
    if (data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          return `"${value.toString().replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center bg-gray-100 rounded-lg p-1 w-full sm:w-auto justify-center sm:justify-start">
        <button
          onClick={() => onViewModeChange("card")}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === "card"
              ? "bg-white text-blue-700 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <Grid3X3 className="w-4 h-4" />
          <span>Tiles</span>
        </button>
        <button
          onClick={() => onViewModeChange("table")}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === "table"
              ? "bg-white text-blue-700 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <List className="w-4 h-4" />
          <span>Table</span>
        </button>
      </div>

      <button
        onClick={() => exportToCSV(data, "employees.csv")}
        className="flex items-center justify-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors bg-white text-blue-700 shadow-sm border border-gray-200 hover:bg-gray-50 w-full sm:w-auto"
      >
        <Download className="w-4 h-4" />
        <span>Download CSV</span>
      </button>
    </div>
  );
};

const EmployeeFilterSystem = () => {
  const [emp_details, setEmp_details] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [loading, setLoading] = useState(true);
  const [selectedNoOfYears, setSelectedNoOfYears] = useState('');
  const [dataReady, setDataReady] = useState(false);
  const pathLocation = useLocation();
  
  
  const { employees, isConnected, clearCache } = useEmployeeStream(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/employees/stream`);
  function splitEmployeeName(name) {
    const parts = name.trim().split(/\s+/);
    const len = parts.length;

    if (len ===2) {
      return {
        firstName: parts[0],
        middleName: "",
        lastName: parts[1],
      };
    }
  
    if (len > 3) {
      return {
        firstName: `${parts[0]} ${parts[1]}`,
        middleName: parts[2],
        lastName: parts[3],
      };
    }
  
    return {
      firstName: parts[0] || "",
      middleName: parts[1] || "",
      lastName: parts[2] || "",
    };
  }

  // Calculate age ranges from employee data
  const ageRange = useMemo(() => {
    if (emp_details?.length === 0) {
      return { min: 18, max: 65 };
    }
    
    const ages = emp_details
      .map(emp => {
        if (!emp.personal_information?.date_of_birth) return null;
        return new Date().getFullYear() - parseInt(emp.personal_information.date_of_birth.split("-")[0]);
      })
      .filter(age => age && age > 0);
    
    if (ages.length === 0) {
      return { min: 18, max: 65 };
    }
    
    return {
      min: Math.min(...ages),
      max: Math.max(...ages)
    };
  }, [emp_details]);

  // Calculate salary ranges from employee data  
  const salaryRange = useMemo(() => {
    if (emp_details?.length === 0) {
      return { min: 0, max: 1000000 };
    }
    
    const salaries = emp_details
      .map(emp => Object.values(emp.salary_and_compensation)[pathLocation.pathname === '/employees/inactive'?0:Object.keys(emp.salary_and_compensation).length - 1]?.ctc)
      .filter(salary => salary && salary > 0);
    
    if (salaries.length === 0) {
      return { min: 0, max: 1000000 };
    }
    
    return {
      min: Math.min(...salaries),
      max: Math.max(...salaries)
    };
  }, [emp_details]);

  // Initialize filters
  const [salaryFilter, setSalaryFilter] = useState({ min: 0, max: 1000000 });
  const [ageFilter, setAgeFilter] = useState({ min: 18, max: 65 });

  // Set initial filter values only when data is ready
  useEffect(() => {
    if (dataReady && (salaryRange.min !== 0 || salaryRange.max !== 1000000)) {
      setSalaryFilter({
        min: salaryRange.min,
        max: salaryRange.max
      });
    }
  }, [salaryRange, dataReady]);

  useEffect(() => {
    if (dataReady && (ageRange.min !== 18 || ageRange.max !== 65)) {
      setAgeFilter({
        min: ageRange.min,
        max: ageRange.max
      });
    }
  }, [ageRange, dataReady]);

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedSetDataReady = debounce(() => setDataReady(true), 3000);

  useEffect(() => {
    const mappedEmployees = employees.map((emp) => ({
      ...emp,
      ...splitEmployeeName(emp.personal_information.employee_name),
    })).filter(emp => pathLocation.pathname === '/employees/inactive'?!emp.isActive:emp.isActive);

    const uniqueEmployees = Array.from(
      new Map(mappedEmployees.map(emp => [emp.eid, {...emp}])).values()
    );

    setEmp_details(uniqueEmployees);
    
    // Only set filtered employees initially, don't apply filters during loading
    if (!dataReady) {
      setFilteredEmployees(uniqueEmployees);
    }
    //remove all applied filters
    
    setLoading(false);
    debouncedSetDataReady();
    // setTimeout(() => {
    //   clearAllFilters()
    // }, 1000);

  }, [employees, dataReady, pathLocation]);

  // Initialize filters based on enabled fields from FILTER_CONFIG
  const getInitialFilters = () => {
    const initialFilters = {};
    
    // Only add filters for enabled fields
    Object.keys(FILTER_CONFIG).forEach(groupKey => {
      if (FILTER_CONFIG[groupKey].enabled && FILTER_CONFIG[groupKey].fields) {
        Object.keys(FILTER_CONFIG[groupKey].fields).forEach(field => {
          if (FILTER_CONFIG[groupKey].fields[field].enabled) {
            initialFilters[field] = [];
          }
        });
      }
    });
    
    return initialFilters;
  };

  const [filters, setFilters] = useState(getInitialFilters());

  // Helper function to calculate experience in years
  const calculateYearsOfService = (dateOfJoining) => {
    if (!dateOfJoining) return 0;
    const joiningDate = new Date(dateOfJoining);
    const currentDate = new Date();
    const diffInMs = currentDate - joiningDate;
    const diffInYears = diffInMs / (1000 * 60 * 60 * 24 * 365.25);
    return Math.floor(diffInYears * 10) / 10; // Round to 1 decimal place
  };

  // Generate filter options only for enabled fields
  const filterOptions = useMemo(() => {
    if (emp_details?.length === 0) {
      return {};
    }

    const options = {};
    
    // Personal Information
    if (FILTER_CONFIG.personalInformation.enabled) {
      if (FILTER_CONFIG.personalInformation.fields.firstName?.enabled) {
        options.firstName = [...new Set(emp_details.map(emp => emp.personal_information?.employee_name.split(' ')[0]).filter(Boolean))];
      }
      if (FILTER_CONFIG.personalInformation.fields.middleName?.enabled) {
        options.middleName = [...new Set(emp_details.map(emp => emp.personal_information?.employee_name.split(' ').length > 2 ? emp.personal_information?.employee_name.split(' ')[1] : '').filter(Boolean))];
      }
      if (FILTER_CONFIG.personalInformation.fields.lastName?.enabled) {
        options.lastName = [...new Set(emp_details.map(emp => emp.personal_information?.employee_name.split(' ')[emp.personal_information?.employee_name.split(' ').length - 1]).filter(Boolean))];
      }
      if (FILTER_CONFIG.personalInformation.fields.employeeName?.enabled) {
        options.employeeName = [...new Set(emp_details.map(emp => emp.personal_information?.employee_name).filter(Boolean))];
      }
      if (FILTER_CONFIG.personalInformation.fields.gender?.enabled) {
        options.gender = [...new Set(emp_details.map(emp => emp.personal_information?.gender).filter(Boolean))];
      }
      if (FILTER_CONFIG.personalInformation.fields.maritalStatus?.enabled) {
        options.maritalStatus = [...new Set(emp_details.map(emp => emp.personal_information?.marital_status).filter(Boolean))];
      }
    }
    
    // Employment Details
    if (FILTER_CONFIG.employmentDetails.enabled) {
      if (FILTER_CONFIG.employmentDetails.fields.designation?.enabled) {
        options.designation = [...new Set(emp_details.map(emp => emp.employment_details?.designation).filter(Boolean))];
      }
      if (FILTER_CONFIG.employmentDetails.fields.grade?.enabled) {
        options.grade = [...new Set(emp_details.map(emp => emp.employment_details?.grade).filter(Boolean))];
      }
      if (FILTER_CONFIG.employmentDetails.fields.location?.enabled) {
        options.location = [...new Set(emp_details.map(emp => emp.employment_details?.location).filter(Boolean))];
      }
      if (FILTER_CONFIG.employmentDetails.fields.businessUnit?.enabled) {
        options.businessUnit = [...new Set(emp_details.map(emp => emp.employment_details?.business_unit).filter(Boolean))];
      }
      if (FILTER_CONFIG.employmentDetails.fields.division?.enabled) {
        options.division = [...new Set(emp_details.map(emp => emp.employment_details?.division).filter(Boolean))];
      }
      if (FILTER_CONFIG.employmentDetails.fields.reportingManager?.enabled) {
        options.reportingManager = [...new Set(emp_details.map(emp => emp.employment_details?.reporting_manager).filter(Boolean))];
      }
      if (FILTER_CONFIG.employmentDetails.fields.department?.enabled) {
        options.department = [...new Set(emp_details.map(emp => emp.employment_details?.department).filter(Boolean))];
      }
      if (FILTER_CONFIG.employmentDetails.fields.lengthOfService?.enabled) {
        // Calculate different types of service lengths
        const serviceData = emp_details.map(emp => {
          const previousOrgExp = parseFloat(emp.employment_details?.total_experience) || 0;
          const currentOrgExp = calculateYearsOfService(emp.employment_details?.date_of_joining);
          const totalExp = previousOrgExp + currentOrgExp;
          
          return {
            previousOrgExp: Math.round(previousOrgExp),
            currentOrgExp: Math.round(currentOrgExp),
            totalExp: Math.round(totalExp)
          };
        });

        options.lengthOfService = {
          previousOrg: [...new Set(serviceData.map(data => data.previousOrgExp).filter(exp => parseFloat(exp) > 0))].sort((a, b) => parseFloat(a) - parseFloat(b)),
          currentOrg: [...new Set(serviceData.map(data => data.currentOrgExp).filter(exp => parseFloat(exp) > 0))].sort((a, b) => parseFloat(a) - parseFloat(b)),
          total: [...new Set(serviceData.map(data => data.totalExp).filter(exp => parseFloat(exp) > 0))].sort((a, b) => parseFloat(a) - parseFloat(b))
        };
      }
    }

    if (FILTER_CONFIG.educationDetails.enabled) {
      if (FILTER_CONFIG.educationDetails.fields.highestQualification?.enabled) {
        options.highestQualification = [...new Set(emp_details.map(emp => emp.education?.higher_education).filter(Boolean))];
      }
      if (FILTER_CONFIG.educationDetails.fields.tenth?.enabled) {
        options.tenth = [...new Set(emp_details.map(emp => emp.education?.tenth?.education_source).filter(Boolean))];
      }
      if (FILTER_CONFIG.educationDetails.fields.twelfth?.enabled) {
        options.twelfth = [...new Set(emp_details.map(emp => emp.education?.twelfth?.education_source).filter(Boolean))];
      }
      if (FILTER_CONFIG.educationDetails.fields.graduation?.enabled) {
        options.graduation = [...new Set(emp_details.map(emp => emp.education?.graduation?.degree).filter(Boolean))];
      }
      if (FILTER_CONFIG.educationDetails.fields.postGraduation?.enabled) {
        options.postGraduation = [...new Set(emp_details.map(emp => emp.education?.post_graduation?.degree).filter(Boolean))];
      }
      if (FILTER_CONFIG.educationDetails.fields.professional?.enabled) {
        options.professional = [...new Set(emp_details.map(emp => emp.education?.professional?.degree).filter(Boolean))];
      }
    }

    return options;
  }, [emp_details]);

  // Main filtering effect - only runs when data is ready
  useEffect(() => {
    if (!dataReady || emp_details?.length === 0) {
      return;
    }
    
    const filtered = emp_details.filter(employee => {
      const filterChecks = [];
      
      // Personal Information filters
      if (filters.employeeName) {
        filterChecks.push(filters.employeeName.length === 0 || filters.employeeName.includes(employee.personal_information?.employee_name));
      }
      if (filters.gender) {
        filterChecks.push(filters.gender.length === 0 || filters.gender.includes(employee.personal_information?.gender));
      }
      if (filters.maritalStatus) {
        filterChecks.push(filters.maritalStatus.length === 0 || filters.maritalStatus.includes(employee.personal_information?.marital_status));
      }
      
      // Employment Details filters
      if (filters.designation) {
        filterChecks.push(filters.designation.length === 0 || filters.designation.includes(employee.employment_details?.designation));
      }
      if (filters.grade) {
        filterChecks.push(filters.grade.length === 0 || filters.grade.includes(employee.employment_details?.grade));
      }
      if (filters.location) {
        filterChecks.push(filters.location.length === 0 || filters.location.includes(employee.employment_details?.location));
      }
      if (filters.businessUnit) {
        filterChecks.push(filters.businessUnit.length === 0 || filters.businessUnit.includes(employee.employment_details?.business_unit));
      }
      if (filters.division) {
        filterChecks.push(filters.division.length === 0 || filters.division.includes(employee.employment_details?.division));
      }
      if (filters.reportingManager) {
        filterChecks.push(filters.reportingManager.length === 0 || filters.reportingManager.includes(employee.employment_details?.reporting_manager));
      }
      if (filters.department) {
        filterChecks.push(filters.department.length === 0 || filters.department.includes(employee.employment_details?.department));
      }
      
      // Updated Length of Service Range Filter Logic
      if (filters.lengthOfService) {
        if (filters.lengthOfService.length === 0) {
          filterChecks.push(true);
        } else {
          const serviceFilters = parseLengthOfServiceFilters(filters.lengthOfService);
          
          const previousOrgExp = parseFloat(employee.employment_details?.total_experience) || 0;
          const currentOrgExp = calculateYearsOfService(employee.employment_details?.date_of_joining);
          const totalExp = previousOrgExp + currentOrgExp;

          let matchesService = true;

          // Check total experience filter
          if (serviceFilters.total) {
            matchesService = matchesService && (
              totalExp >= serviceFilters.total.min && totalExp <= serviceFilters.total.max
            );
          }

          // Check current organization experience filter
          if (serviceFilters.current) {
            matchesService = matchesService && (
              currentOrgExp >= serviceFilters.current.min && currentOrgExp <= serviceFilters.current.max
            );
          }

          // Check previous organization experience filter
          if (serviceFilters.previous) {
            matchesService = matchesService && (
              previousOrgExp >= serviceFilters.previous.min && previousOrgExp <= serviceFilters.previous.max
            );
          }

          filterChecks.push(matchesService);
        }
      }

      // Education filter
      if (filters.highestQualification) {
        filterChecks.push(filters.highestQualification.length === 0 || 
          filters.highestQualification.includes(employee.education?.higher_education));
      }
      if (filters.tenth) {
        filterChecks.push(filters.tenth.length === 0 || 
          filters.tenth.includes(employee.education?.tenth.education_source));
      }
      if (filters.twelfth) {
        filterChecks.push(filters.twelfth.length === 0 || 
          filters.twelfth.includes(employee.education?.twelfth.education_source));
      }
      if (filters.graduation) {
        filterChecks.push(filters.graduation.length === 0 || 
          filters.graduation.includes(employee.education?.graduation.degree));
      }
      if (filters.postGraduation) {
        filterChecks.push(filters.postGraduation.length === 0 || 
          filters.postGraduation.includes(employee.education?.post_graduation.degree));
      }
      if (filters.professional){
        filterChecks.push(filters.professional.length === 0 || 
          filters.professional.includes(employee.education?.professional.degree));
      }
      
      // Salary filter (only if salary compensation is enabled)
      let salaryMatch = true;
      if (FILTER_CONFIG.salaryCompensation.enabled) {
        const employeeSalary = Object.values(employee.salary_and_compensation)[pathLocation.pathname === '/employees/inactive'?0:Object.keys(employee.salary_and_compensation).length - 1]?.ctc || 0;
        salaryMatch = employeeSalary >= salaryFilter.min && employeeSalary <= salaryFilter.max;
      }

      // Age filter (only if age filter is enabled)
      let ageMatch = true;
      if (FILTER_CONFIG.ageFilter.enabled) {
        if (employee.personal_information?.date_of_birth) {
          const employeeAge = new Date().getFullYear() - parseInt(employee.personal_information.date_of_birth.split("-")[0]);
          ageMatch = employeeAge >= ageFilter.min && employeeAge <= ageFilter.max;
        }
      }

      // Superannuation filter
      if (FILTER_CONFIG.superannuationDetails.enabled && selectedNoOfYears > 0) {
        filterChecks.push(((new Date().getFullYear() - parseInt(employee.personal_information.date_of_birth.split("-")[0])) + selectedNoOfYears) >= 59);
      }
      
      return filterChecks.every(check => check) && salaryMatch && ageMatch;
    });
    
    setFilteredEmployees(filtered);
  }, [filters, salaryFilter, ageFilter, emp_details, selectedNoOfYears, dataReady]);

  const updateFilter = (filterType, values) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: values
    }));
  };

  const clearAllFilters = () => {
    setFilters(getInitialFilters());
    setSalaryFilter({
      min: salaryRange.min,
      max: salaryRange.max
    });
    setAgeFilter({
      min: ageRange.min,
      max: ageRange.max
    });
    setSelectedNoOfYears('');
  };

  const getActiveFiltersCount = () => {
    const regularFiltersCount = Object.values(filters).reduce((count, filterArray) => count + filterArray.length, 0);
    
    const salaryFilterActive = (FILTER_CONFIG.salaryCompensation.enabled && 
      (salaryFilter.min !== salaryRange.min || salaryFilter.max !== salaryRange.max) && dataReady) ? 1 : 0;
    
    const ageFilterActive = (FILTER_CONFIG.ageFilter.enabled && 
      (ageFilter.min !== ageRange.min || ageFilter.max !== ageRange.max) && dataReady) ? 1 : 0;
    
    const superannuationFilterActive = (selectedNoOfYears > 0) ? 1 : 0;
    
    return regularFiltersCount + salaryFilterActive + ageFilterActive + superannuationFilterActive;
  };

  const getActiveFilterTags = () => {
    const tags = [];
    Object.entries(filters).forEach(([key, values]) => {
      if (key === 'lengthOfService') {
        // Handle length of service range filters
        if (values.length > 0) {
          const serviceFilters = parseLengthOfServiceFilters(values);
          
          if (serviceFilters.total) {
            tags.push({
              key: 'totalExperience',
              value: `${serviceFilters.total.min} - ${serviceFilters.total.max} years`,
              type: 'lengthOfServiceRange',
              filterType: 'total'
            });
          }
          
          if (serviceFilters.current) {
            tags.push({
              key: 'currentOrgExperience', 
              value: `${serviceFilters.current.min} - ${serviceFilters.current.max} years`,
              type: 'lengthOfServiceRange',
              filterType: 'current'
            });
          }
          
          if (serviceFilters.previous) {
            tags.push({
              key: 'previousOrgExperience',
              value: `${serviceFilters.previous.min} - ${serviceFilters.previous.max} years`, 
              type: 'lengthOfServiceRange',
              filterType: 'previous'
            });
          }
        }
      } else {
        // Handle other regular filters
        values.forEach(value => {
          tags.push({ key, value, type: 'checkbox' });
        });
      }
    });
    
    if (FILTER_CONFIG.salaryCompensation.enabled && 
        (salaryFilter.min !== salaryRange.min || salaryFilter.max !== salaryRange.max) && dataReady) {
      const formatSalary = (amount) => {
        if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
        if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
        return `₹${amount}`;
      };
      tags.push({   
        key: 'salary', 
        value: `${formatSalary(salaryFilter.min)} - ${formatSalary(salaryFilter.max)}`, 
        type: 'range'   
      });
    }

    if (FILTER_CONFIG.ageFilter.enabled && 
        (ageFilter.min !== ageRange.min || ageFilter.max !== ageRange.max) && dataReady) {
      tags.push({   
        key: 'age', 
        value: `${ageFilter.min} - ${ageFilter.max} years`, 
        type: 'range'   
      });
    }

    if (selectedNoOfYears > 0) {
      tags.push({   
        key: 'superannuation', 
        value: `Within ${selectedNoOfYears} years`, 
        type: 'superannuation'   
      });
    }
    
    return tags;
  };

  const removeFilterTag = (filterKey, value, type) => {
    if (type === 'range' && filterKey === 'salary') {
      setSalaryFilter({ 
        min: salaryRange.min,
        max: salaryRange.max 
      });
    } else if (type === 'range' && filterKey === 'age') {
      setAgeFilter({ 
        min: ageRange.min,
        max: ageRange.max 
      });
    } else if (type === 'superannuation') {
      setSelectedNoOfYears('');
    } else if (type === 'lengthOfServiceRange') {
      // Remove the specific length of service range filter
      const currentFilters = filters.lengthOfService || [];
      let filterPrefix;
      
      if (filterKey === 'totalExperience') {
        filterPrefix = 'total:';
      } else if (filterKey === 'currentOrgExperience') {
        filterPrefix = 'current:';
      } else if (filterKey === 'previousOrgExperience') {
        filterPrefix = 'previous:';
      }
      
      const updatedFilters = currentFilters.filter(filter => !filter.startsWith(filterPrefix));
      
      setFilters(prev => ({
        ...prev,
        lengthOfService: updatedFilters
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterKey]: prev[filterKey].filter(v => v !== value)
      }));
    }
  };

  // Check if filters are active for each group (only for enabled groups)
  const hasPersonalFilters = hasEnabledFields('personalInformation') && 
    Object.keys(FILTER_CONFIG.personalInformation.fields).some(field => 
      FILTER_CONFIG.personalInformation.fields[field].enabled && filters[field]?.length > 0
    );
    
  const hasEmploymentFilters = hasEnabledFields('employmentDetails') && 
    (Object.keys(FILTER_CONFIG.employmentDetails.fields).some(field => 
      FILTER_CONFIG.employmentDetails.fields[field].enabled && 
      field !== 'lengthOfService' && 
      filters[field]?.length > 0
    ) || 
    // Check for length of service range filters
    (FILTER_CONFIG.employmentDetails.fields.lengthOfService?.enabled && 
     filters.lengthOfService?.length > 0));
    
  const hasSalaryFilter = FILTER_CONFIG.salaryCompensation.enabled && 
    (salaryFilter.min !== salaryRange.min || salaryFilter.max !== salaryRange.max) && dataReady;

  const hasAgeFilter = FILTER_CONFIG.ageFilter.enabled && 
    (ageFilter.min !== ageRange.min || ageFilter.max !== ageRange.max) && dataReady;

  const hasSuperannuationFilter = FILTER_CONFIG.superannuationDetails.enabled && 
    selectedNoOfYears > 0;

  const hasEducationFilters = hasEnabledFields('educationDetails') && 
    Object.keys(FILTER_CONFIG.educationDetails.fields).some(field => 
      FILTER_CONFIG.educationDetails.fields[field].enabled && filters[field]?.length > 0
    );
  
  const [filteredData, setFilteredData] = useState([]);
  const [viewModal, setViewModal] = useState({ isOpen: false, url: '' });
  const fieldOrder = ["Employee ID","First Name","Middle Name","Last Name","Employee Full Name","Date of Birth","Age","Gender","Grade", "Location","Verified"];
  const flatData = flattenEmployeeDataT(filteredEmployees)
  const [filteredDataCount, setFilteredDataCount] = useState(null);

  useEffect(() => {
    let exportData = filteredEmployees.map(emp => ({name: emp.personal_information?.employee_name}));
    if (getActiveFiltersCount() > 0) {
        const merged = {};
  
        getActiveFilterTags().forEach((item) => {
          if (merged[item.key]) {
            merged[item.key] += `/${item.value}`;
          } else {
            merged[item.key] = item.value;
          }
        });
        setFilteredData(exportData.map((emp) => ({ ...emp, ...merged })));
        setFilteredDataCount(exportData.length);
      }
  }, [filteredEmployees]);
  
  const [employeeViewMode, setEmployeeViewMode] = useState("active");
  const onViewModeChange = (viewMode) => {
    setEmployeeViewMode(viewMode);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading employee data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <EmployeeCheckListModal isOpen={viewModal.isOpen} onClose={() => setViewModal({ isOpen: false, url: '' })} documentUrl={viewModal.url} />
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-gray-600 to-green-300 bg-clip-text text-transparent">Employee Directory</h1>
          <p className="text-gray-600">Filter and search through employee records</p>
          <div className="flex items-center justify-between">
            <button onClick={clearCache} className="mt-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm">Clear Cache</button>
          </div>
        </div>

        {/* Compact Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                  {getActiveFiltersCount()} active
                </span>
              )}
            </div>
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Filter Dropdowns Row - Only show enabled groups */}
          <div className="flex flex-wrap gap-3 mb-4">
            {/* 1. Personal Information */}
            {hasEnabledFields('personalInformation') && (
              <FilterDropdown title="Personal Information" icon={User} isActive={hasPersonalFilters}>
                <div className="grid grid-cols-2 gap-4">
                  {FILTER_CONFIG.personalInformation.fields.employeeName?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Employee Names</h4>
                      <FilterSubSection
                        title="Employee Names"
                        options={filterOptions.employeeName || []}
                        selectedValues={filters.employeeName || []}
                        onChange={(values) => updateFilter('employeeName', values)}
                        hasSearch={FILTER_CONFIG.personalInformation.fields.employeeName.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.personalInformation.fields.gender?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Gender</h4>
                      <FilterSubSection
                        title="Gender"
                        options={filterOptions.gender || []}
                        selectedValues={filters.gender || []}
                        onChange={(values) => updateFilter('gender', values)}
                        hasSearch={FILTER_CONFIG.personalInformation.fields.gender.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.personalInformation.fields.maritalStatus?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Marital Status</h4>
                      <FilterSubSection
                        title="Marital Status"
                        options={filterOptions.maritalStatus || []}
                        selectedValues={filters.maritalStatus || []}
                        onChange={(values) => updateFilter('maritalStatus', values)}
                        hasSearch={FILTER_CONFIG.personalInformation.fields.maritalStatus.hasSearch}
                      />
                    </div>
                  )}
                </div>
              </FilterDropdown>
            )}

            {/* 2. Employment Details */}
            {hasEnabledFields('employmentDetails') && (
              <FilterDropdown title="Employment Details" icon={Briefcase} isActive={hasEmploymentFilters}>
                <div className="grid grid-cols-2 gap-4">
                  {FILTER_CONFIG.employmentDetails.fields.designation?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Designation</h4>
                      <FilterSubSection
                        title="Designation"
                        options={filterOptions.designation || []}
                        selectedValues={filters.designation || []}
                        onChange={(values) => updateFilter('designation', values)}
                        hasSearch={FILTER_CONFIG.employmentDetails.fields.designation.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.employmentDetails.fields.grade?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Grade</h4>
                      <FilterSubSection
                        title="Grade"
                        options={filterOptions.grade || []}
                        selectedValues={filters.grade || []}
                        onChange={(values) => updateFilter('grade', values)}
                        hasSearch={FILTER_CONFIG.employmentDetails.fields.grade.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.employmentDetails.fields.location?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Location</h4>
                      <FilterSubSection
                        title="Location"
                        options={filterOptions.location || []}
                        selectedValues={filters.location || []}
                        onChange={(values) => updateFilter('location', values)}
                        hasSearch={FILTER_CONFIG.employmentDetails.fields.location.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.employmentDetails.fields.businessUnit?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Business Unit</h4>
                      <FilterSubSection
                        title="Business Unit"
                        options={filterOptions.businessUnit || []}
                        selectedValues={filters.businessUnit || []}
                        onChange={(values) => updateFilter('businessUnit', values)}
                        hasSearch={FILTER_CONFIG.employmentDetails.fields.businessUnit.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.employmentDetails.fields.division?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Division</h4>
                      <FilterSubSection
                        title="Division"
                        options={filterOptions.division || []}
                        selectedValues={filters.division || []}
                        onChange={(values) => updateFilter('division', values)}
                        hasSearch={FILTER_CONFIG.employmentDetails.fields.division.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.employmentDetails.fields.reportingManager?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Reporting Manager</h4>
                      <FilterSubSection
                        title="Reporting Manager"
                        options={filterOptions.reportingManager || []}
                        selectedValues={filters.reportingManager || []}
                        onChange={(values) => updateFilter('reportingManager', values)}
                        hasSearch={FILTER_CONFIG.employmentDetails.fields.reportingManager.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.employmentDetails.fields.department?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Department</h4>
                      <FilterSubSection
                        title="Department"
                        options={filterOptions.department || []}
                        selectedValues={filters.department || []}
                        onChange={(values) => updateFilter('department', values)}
                        hasSearch={FILTER_CONFIG.employmentDetails.fields.department.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.employmentDetails.fields.lengthOfService?.enabled && (
                    <div className="col-span-2">
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Length of Service</h4>
                      <LengthOfServiceFilter
                        options={filterOptions.lengthOfService}
                        selectedValues={filters.lengthOfService || []}
                        onChange={(values) => updateFilter('lengthOfService', values)}
                      />
                    </div>
                  )}
                </div>
              </FilterDropdown>
            )}

            {/* 3. Salary and Compensation */}
            {FILTER_CONFIG.salaryCompensation.enabled && (
              <FilterDropdown title="Salary & Compensation" icon={DollarSign} isActive={hasSalaryFilter}>
                <SalaryRangeFilter
                  minSalary={salaryRange.min}
                  maxSalary={salaryRange.max}
                  selectedRange={salaryFilter}
                  onChange={setSalaryFilter}
                />
              </FilterDropdown>
            )}

            {/* 4. Age Filter */}
            {FILTER_CONFIG.ageFilter.enabled && (
              <FilterDropdown title="Age Range" icon={Calendar} isActive={hasAgeFilter}>
                <AgeRangeFilter
                  minAge={ageRange.min}
                  maxAge={ageRange.max}
                  selectedRange={ageFilter}
                  onChange={setAgeFilter}
                />
              </FilterDropdown>
            )}

            {/* 5. Education Details */}
            {hasEnabledFields('educationDetails') && (
              <FilterDropdown title="Education" icon={Book} isActive={hasEducationFilters}>
                <div className="grid grid-cols-1 gap-4">
                  {FILTER_CONFIG.educationDetails.fields.tenth?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Tenth</h4>
                      <FilterSubSection
                        title="Tenth"
                        options={filterOptions.tenth || []}
                        selectedValues={filters.tenth || []}
                        onChange={(values) => updateFilter('tenth', values)}
                        hasSearch={FILTER_CONFIG.educationDetails.fields.tenth.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.educationDetails.fields.twelfth?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Twelfth</h4>
                      <FilterSubSection
                        title="Twelfth"
                        options={filterOptions.twelfth || []}
                        selectedValues={filters.twelfth || []}
                        onChange={(values) => updateFilter('twelfth', values)}
                        hasSearch={FILTER_CONFIG.educationDetails.fields.twelfth.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.educationDetails.fields.graduation?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Graduation</h4>
                      <FilterSubSection
                        title="Graduation"
                        options={filterOptions.graduation || []}
                        selectedValues={filters.graduation || []}
                        onChange={(values) => updateFilter('graduation', values)}
                        hasSearch={FILTER_CONFIG.educationDetails.fields.graduation.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.educationDetails.fields.postGraduation?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Post Graduation</h4>
                      <FilterSubSection
                        title="Post Graduation"
                        options={filterOptions.postGraduation || []}
                        selectedValues={filters.postGraduation || []}
                        onChange={(values) => updateFilter('postGraduation', values)}
                        hasSearch={FILTER_CONFIG.educationDetails.fields.postGraduation.hasSearch}
                      />
                    </div>
                  )}
                  {FILTER_CONFIG.educationDetails.fields.professional?.enabled && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Professional Qualification</h4>
                      <FilterSubSection
                        title="Professional Qualification"
                        options={filterOptions.professional || []}
                        selectedValues={filters.professional || []}
                        onChange={(values) => updateFilter('professional', values)}
                        hasSearch={FILTER_CONFIG.educationDetails.fields.professional.hasSearch}
                      />
                    </div>
                  )}




                </div>
              </FilterDropdown>
            )}

            {/* 6. Superannuation */}
            {FILTER_CONFIG.superannuationDetails.enabled && (
              <FilterDropdown title="Superannuation within (years)" icon={DollarSign} isActive={hasSuperannuationFilter}>
                <SuperannuationFilter selectedYears={selectedNoOfYears} setSelectedYears={setSelectedNoOfYears}/>
              </FilterDropdown>
            )}
          </div>

          {/* Active Filter Tags */}
          {getActiveFilterTags().length > 0 && (
            <div className="border-t border-gray-200 pt-3">
              <div className="flex flex-wrap gap-2">
                {getActiveFilterTags().map((tag, index) => (
                  <span
                    key={`${tag.key}-${tag.value}-${index}`}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag.key === 'salary' ? tag.value : 
                     tag.key === 'age' ? tag.value : 
                     tag.key === 'superannuation' ? tag.value : 
                     tag.type === 'lengthOfServiceRange' ? tag.value :
                     `${tag.key}: ${tag.value}`}
                    <button
                      onClick={() => removeFilterTag(tag.key, tag.value, tag.type)}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:text-blue-600 hover:bg-blue-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">{filteredDataCount===null?emp_details.length:filteredDataCount}</span> of{' '}
              <span className="font-medium text-gray-900">{emp_details.length}</span> employees
            </div>
            {getActiveFiltersCount() > 0 && (
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-600">
                  {getActiveFiltersCount()} filter{getActiveFiltersCount() > 1 ? 's' : ''} applied
                </span>
              </div>
            )}
          </div>
          
          <ViewModeToggle 
            viewMode={viewMode} 
            onViewModeChange={setViewMode}
            data={filteredData}
          />
        </div>

        {/* Results */}
        {filteredEmployees.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your filters to see more results
            </p>
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredEmployees.map((employee, index) => (
                  <EmployeeCard key={employee.employment_details?.employee_code || index} employee={employee} />
                ))}
              </div>
            ) : (
              <EmployeeTableDataGrid 
                rawData={filteredEmployees.length > 0 && flatData.length > 0 ? flatData : []} 
                columnOrderData={filteredEmployees.length > 0 && flatData.length > 0 ? Object.keys(flatData[0]).map((key) => key) : []} 
                baseColumnsData={filteredEmployees.length > 0 && flatData.length > 0 ? Object.keys(flatData[0]).map((key) => ({
                  field: key, 
                  headerName: key, 
                  width: 150, 
                  sortable: true, 
                  filterable: true, 
                  hideable: true, 
                })) : []} 
                hiddenColumnsData={new Set(filteredEmployees.length > 0 && flatData.length > 0 ? Object.keys(flatData[0]).filter((key) => !fieldOrder.includes(key)) : [])} 
                filteredDataCount={setFilteredDataCount}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeFilterSystem;
